import {NamedRange, RangeSize} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/NamedRange';
import {MeshUtils} from '@mintdata-wsp/common/util/MeshUtils';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {FramePath} from '@mintdata/mint-kit/api/values/frames/Frame';
import {Frames} from '@mintdata/mint-kit/api/values/frames/Frames';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {inject, observer} from 'mobx-react';
import * as React from 'react';
import _clamp from 'lodash/clamp';

const styles = require('@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/mesh/Mesh.less');
const cx = classnames.bind(styles);

const NAMED_RANGE_HEADER_HEIGHT:number = 12;
const NAMED_RANGE_FRAME_PADDING:number = 3;

interface IMeshNamedRangesFramesProps
{
    ctx?:IWSPMeshContext;
    isNested?:boolean;
    pathInFrame?:FramePath;
}

type NamedRangeInfo = {
    range:NamedRange,
    isNested:boolean,
    showHeader:boolean,
    style:React.CSSProperties
}

@inject('ctx')
@observer
export class MeshNamedRangesFrames extends React.Component<IMeshNamedRangesFramesProps, any>
{
    // @computed
    private get rangesToRender():Array<NamedRangeInfo>
    {
        const {main, mesh, namedRanges, frameDetails, workbook: workbookCtrl} = controllers.ui.spreadsheets;
        const {ctx, isNested, pathInFrame} = this.props;
        const {workbookId, mainSheetId, sheetId} = ctx;

        const mainSheet = main.findAnySheetById(mainSheetId);
        if( !mainSheet )
            return null;

        const meshState = mesh.getState(ctx);
        const isTopLevelSheet = mainSheetId === sheetId;

        const sheetRanges = namedRanges.getNamedRangesBySheetId(mainSheetId);

        const result = [];
        for( const range of sheetRanges )
        {
            let rangePath = range.framePath;

            let isNested;
            let startRow;
            let startCol;

            if( isTopLevelSheet )
            {
                isNested = !!rangePath;
                startRow = range.location.rowIndex;
                startCol = range.location.colIndex;
            }
            else
            {
                if( !rangePath )
                    continue;

                const frameEntry = frameDetails.getEntryBySheetId(workbookId, mainSheetId, sheetId);
                if( !frameEntry || !range.location.isEqualTo(frameEntry.mainSheetSlotLocation) )
                    continue;

                const entryPath = frameEntry.pathInFrame;
                if( !Frames.isStrictFramePathPrefix(entryPath, rangePath) )
                    continue;

                // next coordinate in rangePath will be the coordinate inside that sheet
                const rangeCoord = rangePath[entryPath.length];

                startRow = rangeCoord.rowIndex;
                startCol = frameEntry.sheet.columnNames.findIndex(name => name === rangeCoord.fieldName);
                if( startCol == -1 )
                    continue;

                // if range is contained in this nested sheet, it's framePath will be exacly 1 longer than sheet's pathInFrame
                isNested = entryPath.length != rangePath.length - 1;
            }

            let rowCount, colCount;
            if( isNested )
                rowCount = colCount = 1;
            else
            {
                const rangeSize = namedRanges.getNamedRangeSize(range.id);
                if( Type.isInstanceOf(rangeSize, Type.Error) )
                    continue;

                rowCount = (rangeSize as RangeSize).rowCount;
                colCount = (rangeSize as RangeSize).colCount;
            }

            const endRow = _clamp(startRow + rowCount - 1, startRow, meshState.rowHeights.length - 1);
            const endCol = _clamp(startCol + colCount - 1, startCol, meshState.colWidths.length - 1);

            const showHeader = !isNested && range.name;
            const style = this._rangeStyle(meshState, startRow, startCol, endRow, endCol, showHeader);

            result.push({
                range,
                isNested,
                showHeader,
                style
            });
        }

        return result;
    }

    private _rangeStyle(meshState, startRow, startCol, endRow, endCol, showHeader):React.CSSProperties
    {
        let {x, y, width, height} = MeshUtils.getRangeBounds2(
            meshState, startRow, startCol, endRow, endCol
        );

        x += NAMED_RANGE_FRAME_PADDING;
        y += NAMED_RANGE_FRAME_PADDING;
        width -= 2 * NAMED_RANGE_FRAME_PADDING;
        height -= 2 * NAMED_RANGE_FRAME_PADDING;

        if( showHeader )
        {
            y -= NAMED_RANGE_HEADER_HEIGHT;
            height += NAMED_RANGE_HEADER_HEIGHT;
        }

        return {
            transform: `translate(${x}px, ${y}px)`,
            width: `${width}px`,
            height: `${height}px`
        };
    }

    @bind
    private _onMouseDown(event:React.MouseEvent<HTMLDivElement>):void
    {
        console.log('MouseDown on namedRange header');
        event.preventDefault();
        event.stopPropagation();
    }

    public render():JSX.Element
    {
        const {main} = controllers.ui.spreadsheets;
        // const {ctx} = this.props;
        // if( !main.areNamedRangesVisible(ctx) )
        //     return null;

        const rangesToRender = this.rangesToRender;
        if( !rangesToRender || rangesToRender.length == 0 )
            return null;

        return <div>
            {
                rangesToRender.map(({range, isNested, showHeader, style}, index) =>

                    <div
                        key={index}
                        style={style}
                        className={cx('namedRange', {isNested, showHeader})}
                    >
                        {
                            showHeader && <div
                                className={cx('namedRangeHeader')}
                                onMouseDown={this._onMouseDown}>
                                {range.name}
                            </div>
                        }
                    </div>
                )
            }
        </div>;
    }
}
